import React, { memo, useCallback, useEffect, useRef, useState } from "react"
import { Box, Text } from "@mos-cat/ds"
import { $isListNode, ListNode } from "@lexical/list"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { $isHeadingNode } from "@lexical/rich-text"
import { $isCodeNode, CODE_LANGUAGE_FRIENDLY_NAME_MAP } from "@lexical/code"

import { $getSelectionStyleValueForProperty } from "@lexical/selection"
import { $findMatchingParent, $getNearestNodeOfType } from "@lexical/utils"
import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_CRITICAL,
  CONTROLLED_TEXT_INSERTION_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from "lexical"

import { ActionsPopover, Button, Tooltip } from "@dit/core-frontend"

import styles from "./toolbarPlugin.module.css"

import { EmailIcon } from "@editor/theme/editorIcons"

import {
  ToolbarContainer,
  blockTypeToBlockName,
  BlockFormatDropDown,
  FontDropDown,
  FormatedList,
  BlockChangeAlign,
  ChangeHistory,
  TextFormat,
  ImageUpload,
} from "./components"
import { TableOfContentButton } from "@src/components/form/Editor/components/ToolbarDropDowns"
import { ArrowDown } from "@mos-cat/ds-icons"
import { MentionButtons } from "./components/MentionButtons"

const buttonProps = {
  kind: "borderless",
  vertSize: "32",
  styles: {
    padding: "0 !important",
    "& > *": {
      display: "flex",
      alignItems: "center",
      gridGap: "5px",
      fontWeight: "400",
      color: "var(--gray-90)",
    },
    "&:hover > *, &:hover > * > *": {
      color: "var(--blue-60)",
    },
  },
}

function getCodeLanguageOptions() {
  const options = []

  for (const [lang, friendlyName] of Object.entries(CODE_LANGUAGE_FRIENDLY_NAME_MAP)) {
    options.push([lang, friendlyName])
  }

  return options
}

const CODE_LANGUAGE_OPTIONS = getCodeLanguageOptions()

export const ToolbarPlugin = memo(
  ({ isDragging, setIsDragging, containerSize, editorRef, maxWidth }) => {
    const [editor] = useLexicalComposerContext()
    const [activeEditor, setActiveEditor] = useState(editor)
    const [fontSize, setFontSize] = useState("12pt")
    const [blockType, setBlockType] = useState("paragraph")
    const [selectedElementKey, setSelectedElementKey] = useState(null)

    const undoRedoRef = useRef(null)
    const alignRef = useRef(null)
    const blockFormatRef = useRef(null)
    const fontSizeRef = useRef(null)
    const textFormatRef = useRef(null)
    const listFormatRef = useRef(null)
    const imageUploadRef = useRef(null)
    const userMentionRef = useRef(null)

    const [isBold, setIsBold] = useState(false)
    const [isItalic, setIsItalic] = useState(false)
    const [isUnderline, setIsUnderline] = useState(false)
    const [isStrikethrough, setIsStrikethrough] = useState(false)

    const [isEditable, setIsEditable] = useState(() => editor.isEditable())

    const updateToolbar = useCallback(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode()
        let element =
          anchorNode.getKey() === "root"
            ? anchorNode
            : $findMatchingParent(anchorNode, (e) => {
                const parent = e.getParent()
                return parent !== null && $isRootOrShadowRoot(parent)
              })

        if (element === null) {
          element = anchorNode.getTopLevelElementOrThrow()
        }

        const elementKey = element.getKey()
        const elementDOM = activeEditor.getElementByKey(elementKey)
        setIsBold(selection.hasFormat("bold"))
        setIsItalic(selection.hasFormat("italic"))
        setIsUnderline(selection.hasFormat("underline"))
        setIsStrikethrough(selection.hasFormat("strikethrough"))

        if (elementDOM !== null) {
          setSelectedElementKey(elementKey)
          if ($isListNode(element)) {
            const parentList = $getNearestNodeOfType(anchorNode, ListNode)
            const type = parentList ? parentList.getListType() : element.getListType()
            setBlockType(type)
          } else {
            const type = $isHeadingNode(element) ? element.getTag() : element.getType()
            if (type in blockTypeToBlockName) {
              setBlockType(type)
            }
          }
        }

        setFontSize($getSelectionStyleValueForProperty(selection, "font-size", "12pt"))
      }
    }, [])

    useEffect(() => {
      return editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateToolbar()
          setActiveEditor(newEditor)
          return false
        },
        COMMAND_PRIORITY_CRITICAL,
      )
    }, [editor, updateToolbar])

    const onCodeLanguageSelect = useCallback(
      (value) => {
        activeEditor.update(() => {
          if (selectedElementKey !== null) {
            const node = $getNodeByKey(selectedElementKey)
            if ($isCodeNode(node)) {
              node.setLanguage(value)
            }
          }
        })
      },
      [activeEditor, selectedElementKey],
    )

    return (
      <ToolbarContainer className={styles.toolbarItems} editorRef={editorRef} maxWidth={maxWidth}>
        <Box display="flex" alignItems="center" gridGap="7px" ref={undoRedoRef}>
          <ChangeHistory
            activeEditor={activeEditor}
            editor={editor}
            updateToolbar={updateToolbar}
            setIsEditable={setIsEditable}
            isEditable={isEditable}
            buttonProps={buttonProps}
          />
        </Box>
        <div aria-hidden="true" className={styles.separator}></div>
        <TableOfContentButton activeEditor={activeEditor} isEditable={isEditable} />
        <div aria-hidden="true" className={styles.separator}></div>
        <Box ref={blockFormatRef}>
          <BlockFormatDropDown
            disabled={!isEditable}
            blockType={blockType}
            editor={editor}
            value={blockType}
            label={blockType}
            editorRef={editorRef}
          />
        </Box>
        {blockType === "code" ? (
          <>
            <ActionsPopover
              disabled={!isEditable}
              portalContainer={document.body}
              content={
                <Button
                  {...buttonProps}
                  kind="borderless"
                  vertSize="30"
                  width="70px"
                  display="flex"
                  justifyContent="start"
                  alignItems="center"
                >
                  <Box display="flex">
                    <Text fontSize="fs-14">язык</Text>
                    <Text fontSize="fs-14">
                      <ArrowDown />
                    </Text>
                  </Box>
                </Button>
              }
            >
              {(buttonProps) => (
                <>
                  {CODE_LANGUAGE_OPTIONS.map(([value, name]) => (
                    <Button
                      {...buttonProps}
                      {...buttonProps}
                      key={value}
                      onClick={() => onCodeLanguageSelect(value)}
                    >
                      {name}
                    </Button>
                  ))}
                </>
              )}
            </ActionsPopover>
          </>
        ) : (
          <>
            <div className={styles.separator}></div>
            <Box ref={fontSizeRef}>
              <FontDropDown
                disabled={!isEditable}
                style={"font-size"}
                value={fontSize}
                editor={editor}
                editorRef={editorRef}
              />
            </Box>
            <div aria-hidden="true" className={styles.separator}></div>
            <Box display="flex" alignItems="center" gridGap="5px" ref={textFormatRef}>
              <TextFormat
                activeEditor={activeEditor}
                buttonProps={buttonProps}
                isEditable={isEditable}
                isBold={isBold}
                isItalic={isItalic}
                isStrikethrough={isStrikethrough}
                isUnderline={isUnderline}
                blockType={blockType}
              />
            </Box>
            <div aria-hidden="true" className={styles.separator}></div>
            <Box ref={listFormatRef}>
              <FormatedList disabled={!isEditable} blockType={blockType} editor={editor} />
            </Box>
            <div aria-hidden="true" className={styles.separator}></div>{" "}
            <Box ref={alignRef} display="flex" alignItems="center" gridGap="10px">
              <BlockChangeAlign
                activeEditor={activeEditor}
                isEditable={isEditable}
                buttonProps={buttonProps}
              />
            </Box>
            <div aria-hidden="true" className={styles.separator}></div>
            <Box display="flex" alignItems="center" gridGap="10px">
              <MentionButtons activeEditor={activeEditor} buttonProps={buttonProps} />
            </Box>
            <div aria-hidden="true" className={styles.separator}></div>
            <Box ref={imageUploadRef}>
              <ImageUpload
                editor={editor}
                setIsDragging={setIsDragging}
                isDragging={isDragging}
                containerSize={containerSize}
                buttonProps={buttonProps}
                isEditable={isEditable}
              />
            </Box>
          </>
        )}
      </ToolbarContainer>
    )
  },
)

ToolbarPlugin.displayName = "ToolbarPlugin"
